// Session Flash Controller
// Rules applied:
// - UI and Styling: Using Hotwire (Stimulus) for dynamic, SPA-like interactions
// - Code Style: Following JavaScript best practices with descriptive variable names

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.checkForFlashMessages()
  }

  checkForFlashMessages() {
    const flashData = sessionStorage.getItem('flash_message')
    
    if (flashData) {
      try {
        const { type, message, timestamp } = JSON.parse(flashData)
        
        // Only process messages that are less than 5 seconds old
        // This prevents showing old messages on page refreshes
        const now = Date.now()
        const messageAge = now - timestamp
        
        if (messageAge < 5000) {
          this.displayFlashMessage(type, message)
        }
        
        // Clear the message from session storage
        sessionStorage.removeItem('flash_message')
      } catch (error) {
        console.error("Error processing flash message:", error)
        sessionStorage.removeItem('flash_message')
      }
    }
  }

  displayFlashMessage(type, message) {
    // Get the flash container
    const flashContainer = document.getElementById('flash')
    if (!flashContainer) return

    // Create the flash message HTML
    const messageHTML = this.createFlashMessageHTML(type, message)
    
    // Insert the flash message
    flashContainer.innerHTML = messageHTML + flashContainer.innerHTML
    
    // Set up auto-dismiss
    const messageElement = flashContainer.querySelector('[data-flash-target="message"]')
    if (messageElement) {
      setTimeout(() => {
        messageElement.style.opacity = '0'
        setTimeout(() => {
          messageElement.remove()
        }, 300)
      }, 5000)
    }
  }

  createFlashMessageHTML(type, message) {
    // Map type to appropriate classes
    const typeClasses = {
      notice: "bg-blue-100 text-blue-900",
      success: "bg-green-100 text-green-900",
      error: "bg-red-100 text-red-900",
      alert: "bg-yellow-100 text-yellow-900"
    }
    
    // Map type to appropriate icon
    const typeIcons = {
      notice: `<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>`,
      success: `<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>`,
      error: `<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg>`,
      alert: `<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg>`
    }
    
    // Get the appropriate classes and icon
    const classes = typeClasses[type] || typeClasses.notice
    const icon = typeIcons[type] || typeIcons.notice
    
    // Create the HTML
    return `
      <div data-controller="flash" class="transition-opacity duration-300 ease-in-out">
        <div data-flash-target="message" class="mb-4 flex items-center p-4 ${classes} rounded relative" role="alert">
          <div class="flex-shrink-0 mr-3">
            ${icon}
          </div>
          <div class="text-sm font-medium flex-grow">
            ${message}
          </div>
          <button type="button" data-action="flash#dismiss" class="ml-auto -mx-1.5 -my-1.5 rounded-lg focus:ring-2 p-1.5 inline-flex h-8 w-8 hover:bg-opacity-25 hover:bg-gray-500" aria-label="Close">
            <span class="sr-only">Close</span>
            <svg aria-hidden="true" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path></svg>
          </button>
        </div>
      </div>
    `
  }
}
